'use client';

import { AlertTriangle, Loader2 } from 'lucide-react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { Member } from '@/lib/query/useOrganizationMembers';

interface RevokeInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member | null;
  onConfirm: () => void;
  isPending?: boolean;
}

export function RevokeInvitationModal({
  isOpen,
  onClose,
  member,
  onConfirm,
  isPending = false,
}: RevokeInvitationModalProps) {
  if (!member) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Revoke Invitation"
      size="sm"
    >
      <div className="space-y-4">
        <div className="bg-destructive/10 flex items-start gap-3 rounded-md p-4">
          <AlertTriangle className="text-destructive mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">
              Are you sure you want to revoke this invitation?
            </p>
            <p className="text-muted-foreground text-sm">
              The invitation to <strong>{member.email}</strong> will be
              cancelled and their invite link will stop working. You can invite
              them again later.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Revoking...
              </>
            ) : (
              'Revoke Invitation'
            )}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
